/**
 * Shared cart bill math.
 *
 * GST can be charged two ways and the outlet decides which (`Outlet.TaxInclusive`, and per item on
 * the menu):
 *  - **Exclusive** (the default): menu prices are the pre-tax base and GST is added ON TOP, so the
 *    customer sees Subtotal + GST + Total.
 *  - **Inclusive**: the menu price is already what the customer pays. The tax is *extracted* for the
 *    invoice, never added, so Total equals Subtotal.
 *
 * This used to be hardcoded to 5% added-on-top, which meant an outlet configured as tax-inclusive
 * had its customer screens quote a total ABOVE the bill it would actually be charged.
 *
 * The coupon discount is validated + computed by the Web API (`GET /api/coupon/validate`) and
 * passed in here as a rupee amount.
 */

/** GST on restaurant food in India — the fallback when the outlet has configured no rate. */
export const GST_PCT = 5;

export interface TaxConfig {
  /** Rate as a percentage (5 = 5%). */
  rate: number;
  /** True when prices already contain the tax. */
  inclusive: boolean;
}

export const DEFAULT_TAX: TaxConfig = { rate: GST_PCT, inclusive: false };

/**
 * The GST on an amount, rounded to the rupee.
 *
 * Exclusive → the tax to ADD on top. Inclusive → the tax already CONTAINED in it (never added).
 */
export function gstOn(amount: number, tax: TaxConfig = DEFAULT_TAX): number {
  const base = Math.max(0, amount);
  if (tax.rate <= 0) return 0;
  return tax.inclusive
    ? Math.round(base - (base * 100) / (100 + tax.rate))
    : Math.round((base * tax.rate) / 100);
}

export interface Charges {
  subTotal: number;
  /** GST added on top (exclusive) or contained in the subtotal (inclusive). */
  gst: number;
  discount: number;
  /** What is actually payable: taxable + gst when exclusive, taxable alone when inclusive. */
  total: number;
  couponApplied: boolean;
  /** Echoed so a caller can decide whether to SHOW the tax line — an inclusive bill adds nothing,
   *  so displaying an amount there reads as an extra charge. */
  inclusive: boolean;
}

/** One priced line, with the tax that applies to it. Undefined fields fall back to `tax`. */
export interface TaxedLine {
  amount: number;
  gst?: number;
  inclusive?: boolean;
}

/** The GST at ONE rate on a bill — everything a "GST 18% · ₹x" row needs. */
export interface GstBucket {
  /** GST rate as a percentage (5 = 5%). */
  rate: number;
  /** Whether the lines at this rate were tax-inclusive (price already contains the GST). */
  inclusive: boolean;
  /** Net (ex-tax) base taxed at this rate, after the pro-rata discount, rounded to the rupee. */
  taxable: number;
  /** GST amount for this rate, rounded to the rupee. */
  tax: number;
}

/**
 * The bill's GST split BY RATE — one bucket per distinct rate that actually applies.
 *
 * This is what makes the breakdown honest: it reads "GST 5% … / GST 18% …" instead of one blended
 * line mislabelled with the outlet's rate, and a line whose rate is 0 (GST not applicable to that
 * item) produces NO bucket, so a no-GST item never shows a rate. Without `lines` the whole subtotal
 * is one bucket at `tax`, which is the pre-per-item behaviour.
 *
 * The discount is spread pro-rata across lines (matching {@link computeCharges}) since attributing a
 * bill-level discount to particular lines would be an invention. Each bucket is rounded to the
 * rupee, so a set of rows always sums to the GST that {@link computeCharges} reports.
 */
export function gstBuckets(
  subTotal: number,
  discount = 0,
  tax: TaxConfig = DEFAULT_TAX,
  lines?: TaxedLine[]
): GstBucket[] {
  const d = Math.max(0, Math.min(discount, subTotal));
  const taxable = subTotal - d;

  const src: TaxedLine[] = lines && lines.length > 0 ? lines : [{ amount: subTotal }];
  const gross = src.reduce((s, l) => s + Math.max(0, l.amount), 0);
  const keep = gross > 0 ? taxable / gross : 1; // pro-rata discount factor

  // Accumulate raw (unrounded) figures per (rate, inclusive), then round once at the end so the
  // rows reconcile with the total.
  const byKey = new Map<string, { rate: number; inclusive: boolean; taxable: number; tax: number }>();
  for (const l of src) {
    const rate = l.gst ?? tax.rate;
    if (rate <= 0) continue; // GST not applicable to this line → it contributes no rate row
    const inclusive = l.inclusive ?? tax.inclusive;
    const net = Math.max(0, l.amount) * keep;
    // Inclusive → the price already contains the tax, so the base is the extracted net; exclusive →
    // the base is the price and the tax is added on top.
    const base = inclusive ? (net * 100) / (100 + rate) : net;
    const t = (base * rate) / 100;
    const key = `${rate}|${inclusive ? 'i' : 'e'}`;
    const acc = byKey.get(key);
    if (acc) {
      acc.taxable += base;
      acc.tax += t;
    } else {
      byKey.set(key, { rate, inclusive, taxable: base, tax: t });
    }
  }

  return [...byKey.values()]
    .sort((a, b) => a.rate - b.rate)
    .map((b) => ({ rate: b.rate, inclusive: b.inclusive, taxable: Math.round(b.taxable), tax: Math.round(b.tax) }));
}

/**
 * Bill totals.
 *
 * Pass `lines` to tax each one at ITS OWN rate — a bill can mix cooked food at 5% with a packaged
 * drink at 18%, and one rate applied to the whole subtotal charges both wrongly. Without `lines`
 * the whole subtotal is taxed at `tax`, which is what every caller did before per-item GST existed.
 *
 * Built on {@link gstBuckets} so the per-rate rows a screen renders always add up to the `gst` here.
 * A discount is applied to the subtotal before tax; with mixed lines it is spread pro-rata.
 */
export function computeCharges(
  subTotal: number,
  discount = 0,
  tax: TaxConfig = DEFAULT_TAX,
  lines?: TaxedLine[]
): Charges {
  const d = Math.max(0, Math.min(discount, subTotal));
  const taxable = subTotal - d;

  const buckets = gstBuckets(subTotal, discount, tax, lines);
  const gst = buckets.reduce((s, b) => s + b.tax, 0);
  // Only exclusive buckets ADD to what's payable; inclusive tax is already inside the price.
  const added = buckets.filter((b) => !b.inclusive).reduce((s, b) => s + b.tax, 0);
  const anyExclusive = buckets.some((b) => !b.inclusive);

  return {
    subTotal,
    gst,
    discount: d,
    total: taxable + added,
    couponApplied: d > 0,
    // No lines → honour the passed config; with lines, a tax row shows only when something was added.
    inclusive: !lines || lines.length === 0 ? tax.inclusive : !anyExclusive
  };
}
